import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    forwardRef,
    Input,
    Output,
    ViewEncapsulation,
} from '@angular/core';
import {
    AbstractControl,
    ControlValueAccessor,
    FormBuilder,
    FormControl,
    FormGroup,
    NG_VALUE_ACCESSOR,
    Validators,
} from '@angular/forms';
import {
    ClrType,
    InputDefinition,
    InputType,
} from '@components/shared/ui-parameter-collection/clr-type.definitions';
import { IPackageArgument } from '@models';

import cloneDeep from 'lodash-es/cloneDeep';

interface IArgument extends IPackageArgument {
    clrType: ClrType.IClrType;
    id: number;
}

export namespace ParameterValidators {
    export const required = (parameterMap: IPackageArgument[]) => {
        return (c: AbstractControl) => {
            const isRequirementSatisfied = (parameterMap || [])
                .every(field => {
                    if (!field.required) { return true; }

                    const hasSource = field.source != null;

                    const hasValue = !!c.value &&
                        c.value[field.name] != null &&
                        c.value[field.name] !== '';

                    return hasSource ||
                        hasValue;
                });

            return isRequirementSatisfied ?
                null : {
                    required: true,
                };
        };
    };
}

@Component({
    selector: 'ui-parameter-collection',
    templateUrl: './ui-parameter-collection.component.html',
    styleUrls: [
        './ui-parameter-collection.component.scss',
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    providers: [{
        provide: NG_VALUE_ACCESSOR,
        useExisting: forwardRef(() => UiParameterCollectionComponent),
        multi: true,
    }],
})
export class UiParameterCollectionComponent implements ControlValueAccessor {
    public InputType = InputType;

    @Input()
    public get collection() {
        return this._data.slice(0);
    }
    public set collection(value) {
        const clone = cloneDeep(value) || [];

        clone.forEach((argument: IArgument, idx: number) => {
            argument.clrType = ClrType.parseType(argument.type);
            argument.id = idx;
        });

        this._data = clone;
        this.expandedRow = null;
        this._updateRowValues();
    }

    @Input()
    public type!: 'input' | 'output';

    @Input()
    public editable = false;

    @Output()
    public closed = new EventEmitter();

    public get isOpen() {
        return !!this.expandedRow;
    }

    public formGroup: FormGroup;
    public inputDefinition: InputDefinition | null = null;
    public value: Record<string, any> | null = null;
    public expandedRow: IArgument | null = null;

    private _data: IArgument[] = [];

    constructor(
        fb: FormBuilder,
        private _cd: ChangeDetectorRef,
    ) {
        this.formGroup = fb.group({});
    }

    public onChange: () => void = () => { };
    public onTouch: () => void = () => { };

    public writeValue(value: any) {
        if (typeof value === 'object') {
            this.value = value;
        } else if (typeof value === 'string') {
            try {
                this.value = JSON.parse(value);
            } catch {
                this.value = null;
            }
        } else {
            this.value = null;
        }

        this._updateRowValues();
        this._cd.markForCheck();
    }

    public registerOnChange(fn: (_: any) => void): void {
        this.onChange = () => {
            this.value = {};
            this._data
                .forEach((argument: IArgument) => {
                    this.value![argument.name] = argument.value;
                });
            fn(this.value);
        };
    }

    public registerOnTouched(fn: () => void): void {
        this.onTouch = fn;
    }

    public editEntry(argument: IArgument) {
        Object.keys(this.formGroup.controls)
            .forEach(key => this.close({
                name: key,
            } as IArgument));
        this._setFormGroup(argument);
        this.expandedRow = argument;
    }

    public close(argument: IArgument) {
        this.formGroup.removeControl(argument.name);
        this.expandedRow = null;
        this.inputDefinition = null;
        this.onTouch();
        this.closed.emit();
    }

    public save(argument: IArgument) {
        if (this.formGroup.invalid) {
            return;
        }

        const value = this.formGroup.value[argument.name];
        this.value![argument.name] = value === '' ?
            null :
            this._reverseMap(this.inputDefinition!.type, value);
        this._updateRowValues();

        this.close(argument);
        this.onChange();
    }

    public clear(argument: IArgument) {
        delete this.value![argument.name];

        this._updateRowValues();
        this.onChange();
    }

    private _setFormGroup(argument: IArgument) {
        const validators = [];

        if (argument.required) {
            validators.push(
                Validators.required,
            );
        }

        this.inputDefinition = ClrType.inputConfiguration(argument.type);

        validators.push(this.inputDefinition.validator);

        const formControl = new FormControl(
            this._valueForType(this.inputDefinition.type, argument.value),
            validators,
        );

        this.formGroup.addControl(argument.name, formControl);
    }

    private _valueForType(type: InputType, value: any) {
        switch (type) {
            case InputType.Boolean:
                if (typeof value === 'string') {
                    return value.toLowerCase() === 'true' ?
                        true :
                        value.toLowerCase() === 'false' ?
                            false :
                            null;
                }
                return value;
            case InputType.Complex:
                return JSON.stringify(value, null, 4);
            default:
                return value;
        }
    }

    private _reverseMap(type: InputType, value: any) {
        switch (type) {
            case InputType.Complex:
                return JSON.parse(value);
            case InputType.Number:
                return Number(value);
            default:
                return value;
        }
    }

    private _updateRowValues() {
        if (
            this._data instanceof Array &&
            this.value instanceof Object
        ) {
            this._data.forEach(argument => {
                argument.value = this.value![argument.name];
            });
        }
    }
}
